/**
 * Step-by-step credential guides per provider, as pure data so the modal,
 * tests and docs share one source of truth. Scopes mirror what the backend
 * actually requests (`apps/api/src/slack/oauth.ts`, GitHub REST calls in
 * `apps/api/src/github/pullRequestService.ts`).
 */

import type { ConnectableSource } from '../../domain/sourceConnection';

export interface SetupGuideStep {
  title: string;
  detail: string;
  /** External link the step points to. */
  href?: string;
  hrefLabel?: string;
  /** Verbatim text the user needs (scopes, manifest, URL). Copyable. */
  code?: string;
  codeLabel?: string;
}

export interface ProviderSetupGuide {
  source: ConnectableSource;
  title: string;
  intro: string;
  steps: SetupGuideStep[];
  /** What Zunelo can and cannot do with the credential. */
  permissionsNote: string;
}

/** User-token scopes Zunelo requests from Slack (keep in sync with the API). */
export const SLACK_USER_SCOPES = [
  'channels:history',
  'groups:history',
  'im:history',
  'mpim:history',
  'chat:write',
  'users:read',
  'search:read',
] as const;

export const SLACK_APP_MANIFEST = `display_information:
  name: Zunelo
  description: Capa personal de atención para hilos y menciones
  background_color: "#4f46e5"
oauth_config:
  scopes:
    user:
${SLACK_USER_SCOPES.map((scope) => `      - ${scope}`).join('\n')}
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false
  token_rotation_enabled: false
`;

/** Classic PAT scopes that cover search, pulls, reviews, checks and merges. */
export const GITHUB_PAT_SCOPES = ['repo', 'read:org'] as const;

export const PROVIDER_SETUP_GUIDES: Record<ConnectableSource, ProviderSetupGuide> = {
  slack: {
    source: 'slack',
    title: 'Cómo obtener tu token de usuario de Slack',
    intro:
      'Zunelo lee tus hilos y responde con tu identidad usando un token de usuario (xoxp-…). Se crea instalando una app de Slack en tu workspace. Toma unos 3 minutos y no necesitas permisos de administrador salvo que tu workspace restrinja la instalación de apps.',
    steps: [
      {
        title: 'Crea una app desde el manifest',
        detail:
          'Abre la consola de apps de Slack, pulsa "Create New App" → "From a manifest", elige tu workspace y pega el manifest en formato YAML. Ya incluye exactamente los permisos que Zunelo necesita.',
        href: 'https://api.slack.com/apps?new_app=1',
        hrefLabel: 'Abrir api.slack.com/apps',
        code: SLACK_APP_MANIFEST,
        codeLabel: 'Manifest (YAML)',
      },
      {
        title: 'Revisa los permisos de usuario',
        detail:
          'En "OAuth & Permissions" → "User Token Scopes" deben aparecer estos scopes. Si creaste la app a mano en vez de con el manifest, agrégalos uno a uno.',
        code: SLACK_USER_SCOPES.join('\n'),
        codeLabel: 'User Token Scopes',
      },
      {
        title: 'Instala la app en tu workspace',
        detail:
          'En la misma página pulsa "Install to Workspace" y autoriza. Slack mostrará el "User OAuth Token", que empieza por xoxp-.',
      },
      {
        title: 'Pega el token en Zunelo',
        detail:
          'Copia el User OAuth Token, pégalo en el campo de Slack en Settings y guarda. Luego usa "Probar conexión": debe mostrar tu usuario de Slack.',
      },
    ],
    permissionsNote:
      'El token solo puede ver lo que tu usuario ya ve (canales, grupos privados y DMs donde estás) y enviar mensajes en tu nombre. Se guarda cifrado en el servidor y nunca vuelve al navegador. Puedes revocarlo desde la app de Slack en cualquier momento.',
  },
  jira: {
    source: 'jira',
    title: 'Cómo conectar Jira con un API token',
    intro:
      'Zunelo se conecta a Jira Cloud con tres datos: la URL de tu sitio, el email de tu cuenta Atlassian y un API token personal. No hace falta ser administrador.',
    steps: [
      {
        title: 'Crea un API token en Atlassian',
        detail:
          'Abre la página de tokens de tu cuenta, pulsa "Create API token", ponle un nombre (por ejemplo "Zunelo") y copia el valor. Solo se muestra una vez.',
        href: 'https://id.atlassian.com/manage-profile/security/api-tokens',
        hrefLabel: 'Abrir id.atlassian.com',
      },
      {
        title: 'Identifica la URL de tu sitio',
        detail:
          'Es la dirección con la que entras a Jira, sin rutas: por ejemplo https://tu-org.atlassian.net.',
        code: 'https://tu-org.atlassian.net',
        codeLabel: 'Formato de site URL',
      },
      {
        title: 'Usa el email de tu cuenta Atlassian',
        detail:
          'Debe ser el mismo email con el que inicias sesión en Jira; el token se valida contra esa cuenta.',
      },
      {
        title: 'Guarda los tres datos en Zunelo',
        detail:
          'En Settings → Jira completa site URL, email y API token, guarda y pulsa "Probar conexión". Debe mostrar tu nombre en Jira.',
      },
    ],
    permissionsNote:
      'El API token tiene los mismos permisos que tu usuario en Jira: Zunelo lee los tickets que puedes ver y solo escribe (transiciones, comentarios) cuando tú lo pides. Puedes revocarlo desde la misma página de Atlassian.',
  },
  github: {
    source: 'github',
    title: 'Cómo crear un Personal Access Token de GitHub',
    intro:
      'Zunelo lista tus pull requests, revisiones pendientes y checks, y puede aprobar, mergear o relanzar CI cuando tú lo pides. Para eso necesita un Personal Access Token (PAT) tuyo.',
    steps: [
      {
        title: 'Abre la creación de tokens clásicos',
        detail:
          'Ve a Settings → Developer settings → Personal access tokens → Tokens (classic) y pulsa "Generate new token (classic)". Los tokens clásicos son los que funcionan con la búsqueda de PRs entre organizaciones.',
        href: 'https://github.com/settings/tokens/new',
        hrefLabel: 'Abrir github.com/settings/tokens',
      },
      {
        title: 'Marca los scopes necesarios',
        detail:
          '"repo" da acceso a PRs, reviews, checks y merge en repos privados y públicos. "read:org" permite buscar PRs en repos de tus organizaciones. No hace falta nada más.',
        code: GITHUB_PAT_SCOPES.join('\n'),
        codeLabel: 'Scopes',
      },
      {
        title: 'Autoriza SSO si tu organización lo exige',
        detail:
          'Si tu organización usa SAML SSO, tras generar el token pulsa "Configure SSO" junto al token y autorízalo para esa organización; si no, GitHub responderá 403.',
      },
      {
        title: 'Pega el token en Zunelo',
        detail:
          'Copia el token (empieza por ghp_), pégalo en Settings → GitHub, guarda y pulsa "Probar conexión". Debe mostrar tu login de GitHub.',
      },
    ],
    permissionsNote:
      'El PAT actúa con tu identidad: Zunelo nunca lo usa para acciones que no dispares tú. Se guarda cifrado en el servidor. Puedes revocarlo desde GitHub en cualquier momento.',
  },
  gmail: {
    source: 'gmail',
    title: 'Cómo conectar Gmail con tu cuenta de Google',
    intro:
      'Zunelo trae correos relevantes a tu Radar, los marca como leídos y puede responderlos, todo con tu identidad. Se conecta con OAuth de Google — no necesitas crear ni pegar ningún token manualmente.',
    steps: [
      {
        title: 'Pulsa "Conectar con Google"',
        detail:
          'En Settings → Gmail, pulsa el botón de conexión. Se abre la pantalla de consentimiento de Google donde eliges tu cuenta.',
        href: 'https://myaccount.google.com/permissions',
        hrefLabel: 'Ver accesos de terceros en tu cuenta de Google',
      },
      {
        title: 'Revisa los permisos solicitados',
        detail:
          'Google mostrará los permisos de lectura, marcado y envío de Gmail que Zunelo pide. Acéptalos para continuar.',
      },
      {
        title: 'Confirma la cuenta conectada',
        detail:
          'Zunelo verifica el correo de la cuenta autorizada antes de guardar la conexión; si no coincide con la que esperabas, desconecta y repite con la cuenta correcta.',
      },
      {
        title: 'Ajusta tus reglas de atención',
        detail:
          'En la misma sección, elige un preset (Recomendado, Bandeja completa, Solo destacados) o personaliza qué correos cuentan como atención — la vista previa de búsqueda muestra exactamente qué se traerá.',
      },
    ],
    permissionsNote:
      'Zunelo solo lee, marca como leídos y responde correos con tu identidad — nunca los borra ni accede a otras cuentas de Google. Los tokens se guardan cifrados en el servidor. Puedes revocar el acceso en cualquier momento desde la página de permisos de tu cuenta de Google.',
  },
};

export function providerSetupGuide(source: ConnectableSource): ProviderSetupGuide {
  return PROVIDER_SETUP_GUIDES[source];
}
